import React, { forwardRef } from 'react';

import type {
  ButtonVariant,
  ButtonSize,
} from 'components/partials/button/button';
import { buttonVariants } from 'components/partials/button/button';

const ICON_SIZE: Record<ButtonSize, number> = {
  baby: 12,
  xs: 16,
  sm: 16,
  md: 20,
  lg: 20,
};

const SPINNER_SIZE: Record<ButtonSize, string> = {
  baby: 'size-3',
  xs: 'size-4',
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-5',
};

interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon: React.ReactNode;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  'data-testid'?: string;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      variant = 'reserve-text',
      size = 'xs',
      icon,
      loading = false,
      onClick,
      disabled,
      type = 'button',
      form,
      className,
      'data-testid': dataTestId = 'icon-button',
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const iconSize = ICON_SIZE[size] || 16;

    return (
      <button
        className={buttonVariants({
          variant,
          size,
          className: `icon-btn icon-btn-size-${size} ${className || ''}`,
        })}
        onClick={onClick}
        disabled={isDisabled}
        ref={ref}
        type={type}
        form={form}
        data-testid={dataTestId}
        {...rest}
      >
        {loading ? (
          <span
            className={`inline-block animate-spin border-2 border-current border-t-transparent rounded-full ${SPINNER_SIZE[size]}`}
          />
        ) : (
          <span
            className="icon-btn-icon"
            style={{ width: iconSize, height: iconSize }}
          >
            {icon}
          </span>
        )}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
